/**
 * Transform Editor Component
 *
 * Manages the transform pipeline for a binary field.
 * Supports scale, offset, expression, lookup, round, and mask transforms.
 */

import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { useBinaryFormatStore } from '@/stores/binary-formats/binary-format-store';
import { TRANSFORM_TYPE_INFO } from '@/types/binaryFormat';
import type { Transform, TransformType } from '@/types/binaryFormat';

interface TransformEditorProps {
  fieldId: string;
  transforms: Transform[];
}

export const TransformEditor: React.FC<TransformEditorProps> = ({
  fieldId,
  transforms,
}) => {
  const { addTransform, updateTransform, deleteTransform } =
    useBinaryFormatStore();

  const handleAdd = () => {
    addTransform(fieldId, { type: 'scale', value: 1 });
  };

  const handleMoveUp = (index: number) => {
    if (index > 0) {
      // Swap transforms by updating both
      const currentTransform = { ...transforms[index] };
      const prevTransform = { ...transforms[index - 1] };
      updateTransform(fieldId, index, prevTransform);
      updateTransform(fieldId, index - 1, currentTransform);
    }
  };

  const handleMoveDown = (index: number) => {
    if (index < transforms.length - 1) {
      // Swap transforms by updating both
      const currentTransform = { ...transforms[index] };
      const nextTransform = { ...transforms[index + 1] };
      updateTransform(fieldId, index, nextTransform);
      updateTransform(fieldId, index + 1, currentTransform);
    }
  };

  return (
    <div className="space-y-2">
      {transforms.length === 0 ? (
        <p className="text-xs text-muted-foreground py-2">
          No transforms. Raw value will be used.
        </p>
      ) : (
        <div className="space-y-2">
          {transforms.map((transform, index) => (
            <TransformItem
              key={index}
              fieldId={fieldId}
              transform={transform}
              index={index}
              totalCount={transforms.length}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              onDelete={() => deleteTransform(fieldId, index)}
            />
          ))}
        </div>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleAdd}
        className="w-full"
      >
        <Plus className="h-3 w-3 mr-1" />
        Add Transform
      </Button>
    </div>
  );
};

// ================================
// Individual Transform Item
// ================================

interface TransformItemProps {
  fieldId: string;
  transform: Transform;
  index: number;
  totalCount: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}

const TransformItem: React.FC<TransformItemProps> = ({
  fieldId,
  transform,
  index,
  totalCount,
  onMoveUp,
  onMoveDown,
  onDelete,
}) => {
  const { updateTransform } = useBinaryFormatStore();
  const [lookupText, setLookupText] = useState(() => {
    if (transform.type === 'lookup' && typeof transform.value === 'object') {
      return Object.entries(transform.value as Record<number, string>)
        .map(([k, v]) => `${k}=${v}`)
        .join(', ');
    }
    return '';
  });

  const typeInfo = TRANSFORM_TYPE_INFO.find((t) => t.type === transform.type);

  const handleTypeChange = (newType: TransformType) => {
    // Reset value to default for new type
    let defaultValue: number | string | Record<number, string> = 1;
    switch (newType) {
      case 'scale':
      case 'offset':
        defaultValue = 1;
        break;
      case 'round':
        defaultValue = 2;
        break;
      case 'mask':
        defaultValue = 0xff;
        break;
      case 'expression':
        defaultValue = '$value';
        break;
      case 'lookup':
        defaultValue = {};
        break;
    }
    updateTransform(fieldId, index, { type: newType, value: defaultValue });
  };

  const handleValueChange = (newValue: string) => {
    const info = TRANSFORM_TYPE_INFO.find((t) => t.type === transform.type);
    if (!info) return;

    let parsedValue: number | string | Record<number, string>;

    switch (info.valueType) {
      case 'number': {
        // Handle hex notation (0x prefix)
        if (newValue.toLowerCase().startsWith('0x')) {
          parsedValue = parseInt(newValue, 16);
        } else {
          parsedValue = parseFloat(newValue) || 0;
        }
        break;
      }
      case 'string':
        parsedValue = newValue;
        break;
      case 'lookup':
        // Parse "0=Off, 1=On" format
        parsedValue = parseLookupString(newValue);
        setLookupText(newValue);
        break;
      default:
        parsedValue = newValue;
    }

    updateTransform(fieldId, index, { value: parsedValue });
  };

  const getDisplayValue = (): string => {
    if (transform.type === 'lookup') {
      return lookupText;
    }
    if (transform.type === 'mask' && typeof transform.value === 'number') {
      return '0x' + transform.value.toString(16).toUpperCase();
    }
    return String(transform.value ?? '');
  };

  return (
    <Card className="p-2 bg-muted/20">
      <div className="flex items-start gap-2">
        {/* Reorder Controls */}
        <div className="flex flex-col gap-0.5 pt-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            onClick={onMoveUp}
            disabled={index === 0}
          >
            <ChevronUp className="h-3 w-3" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-4 w-4"
            onClick={onMoveDown}
            disabled={index === totalCount - 1}
          >
            <ChevronDown className="h-3 w-3" />
          </Button>
        </div>

        {/* Step Number */}
        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-muted text-xs font-medium mt-1.5">
          {index + 1}
        </div>

        {/* Transform Type & Value */}
        <div className="flex-1 grid grid-cols-2 gap-2">
          <Select value={transform.type} onValueChange={handleTypeChange}>
            <SelectTrigger className="h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSFORM_TYPE_INFO.map((info) => (
                <SelectItem key={info.type} value={info.type}>
                  {info.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            className="h-8"
            value={getDisplayValue()}
            onChange={(e) => handleValueChange(e.target.value)}
            placeholder={typeInfo?.placeholder || ''}
          />
        </div>

        {/* Delete Button */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Help Text */}
      {typeInfo && (
        <p className="text-xs text-muted-foreground mt-1 pl-12">
          {typeInfo.description}
          {transform.type === 'expression' && ' (use $value for input)'}
          {transform.type === 'lookup' && ' (format: 0=Off, 1=On, 2=Auto)'}
        </p>
      )}
    </Card>
  );
};

// ================================
// Utility Functions
// ================================

/**
 * Parse a lookup string like "0=Off, 1=On, 2=Auto" into a Record
 */
function parseLookupString(input: string): Record<number, string> {
  const result: Record<number, string> = {};

  if (!input.trim()) {
    return result;
  }

  const pairs = input.split(',').map((s) => s.trim());
  for (const pair of pairs) {
    const match = pair.match(/^(\d+)\s*=\s*(.+)$/);
    if (match) {
      const key = parseInt(match[1], 10);
      const value = match[2].trim();
      result[key] = value;
    }
  }

  return result;
}

export default TransformEditor;
